import React from 'react';
import { SeatsChart } from 'services/Seats/types';
import { useGetSeatsCharts } from 'hooks/useGetSeatsCharts';
import { ChartItem } from './ChartItem/ChartItem';
import styles from './PunchupSeatsChartPicker.module.css';

export interface PunchupSeatsChartPickerProps {
	onChartSelect?: (chart: SeatsChart) => void;
	selectedChartId?: string;
	title?: string;
	/** Optional muted sub-label rendered under the title. */
	description?: string;
	/**
	 * Freeze the selection (e.g. once tickets have sold): the selected chart stays highlighted
	 * but cannot be changed, and the other charts are greyed out and non-interactive.
	 */
	locked?: boolean;
}

/**
 * Green-owned seating-chart picker — a grid of selectable seats.io chart cards. Cloned from the
 * red/shared `features/SeatsCharts/SeatsChartsList` so the green hall-change flow carries no
 * dark-theme dependency and lifts cleanly into downstroke (P5).
 */
export const PunchupSeatsChartPicker: React.FC<PunchupSeatsChartPickerProps> = ({
	onChartSelect,
	selectedChartId,
	title = 'Seat Charts',
	description,
	locked = false,
}) => {
	const { data: charts, isLoading, error } = useGetSeatsCharts();

	// Only the inner body varies by state; the section shell + title + description render once.
	let body: React.ReactNode;
	if (isLoading) {
		body = <div className={styles.stateMessage}>Loading seat charts...</div>;
	} else if (error) {
		body = (
			<div className={`${styles.stateMessage} ${styles.stateError}`}>Failed to load seat charts. Please try again.</div>
		);
	} else if (charts.length === 0) {
		body = <div className={styles.stateMessage}>No seat charts available</div>;
	} else {
		body = (
			<div className={styles.list}>
				{charts.map((chart) => (
					<ChartItem
						key={chart.id}
						chart={chart}
						selected={selectedChartId === chart.key}
						locked={locked}
						onSelect={onChartSelect}
					/>
				))}
			</div>
		);
	}

	return (
		<div className={styles.section}>
			<h2 className={styles.title}>{title}</h2>
			{description && <p className={styles.description}>{description}</p>}
			{body}
		</div>
	);
};
